/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import {
    Button,
    Icon,
    Text,
    Heading,
    NumberField,
    StyledModal,
    ModalActions,
    ButtonGroup,
    Alert,
    Box,
} from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import GroupMemberItem from './GroupMemberItem';
import FeeTypeInfo from './FeeTypeInfo';
import { createAvailabilityRangeMap, issuanceStatuses } from '../../utils/permitUtilsNew';
import * as types from '../../constants';

class GroupMemberList extends React.Component {
    static propTypes = {
        permit: PropTypes.object.isRequired,
        issuance: PropTypes.object,
        division: PropTypes.object.isRequired,
        entryDate: PropTypes.instanceOf(gnoment),
        exitDate: PropTypes.shape({}),
        groupMembers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
        altGroupLeaders: PropTypes.arrayOf(PropTypes.shape({})),
        maxAlternateLeaders: PropTypes.number,
        maxMemberQuota: PropTypes.number,
        handleGroupMemberChange: PropTypes.func.isRequired,
        isDisabled: PropTypes.bool,
    };

    static defaultProps = {
        maxMemberQuota: 99999,
    };

    constructor(props) {
        super(props);

        this.addMember = this.addMember.bind(this);
        this.removeMember = this.removeMember.bind(this);
        this.handleMemberInfoChange = this.handleMemberInfoChange.bind(this);
        this.handleGroupSizeChange = this.handleGroupSizeChange.bind(this);
        this.onCloseMessageModal = this.onCloseMessageModal.bind(this);
        this.onOpenMessageModal = this.onOpenMessageModal.bind(this);
        this.state = {
            isMessageModalOpen: false,
            memberCount: this.props.groupMembers.length,
            focusOnNextAdd: false,
        };
    }

    UNSAFE_componentWillReceiveProps() {
        this.setState({ memberCount: this.props.groupMembers.length });
    }

    handleGroupSizeChange(e) {
        const { memberCount } = this.state;
        const newMemberCount = parseInt(e.target.value, 10);

        if (newMemberCount > memberCount) {
            // add members
            this.addMember(newMemberCount - memberCount);
        } else if (newMemberCount < memberCount) {
            // remove members from end of list
            const newGroupMembers = this.props.groupMembers;

            if (newMemberCount < 1) {
                // remove all members but the leader
                this.props.handleGroupMemberChange(newGroupMembers[0]);
            } else {
                const numToSplice = memberCount - newMemberCount;

                newGroupMembers.splice(-numToSplice);

                this.props.handleGroupMemberChange(newGroupMembers);
            }
        }
    }

    addMember(numMembersToAdd) {
        const newGroupMembers = this.props.groupMembers;
        const { maxMemberQuota } = this.props;
        const currentCount = parseInt(this.state.memberCount, 10);

        let condition = false;
        let diff = 0;
        if (Number.isInteger(numMembersToAdd)) {
            // add the amount
            condition = currentCount + numMembersToAdd <= maxMemberQuota;
            diff = numMembersToAdd;

            this.setState({ focusOnNextAdd: false });
        } else {
            // add one
            condition = currentCount + 1 <= maxMemberQuota;
            diff = 1;

            this.setState({ focusOnNextAdd: true });
        }

        if (condition) {
            for (let i = 0; i < diff; i += 1) {
                const newMember = {
                    id: newGroupMembers.length + 1,
                    removable: true,
                    startDate: this.props.entryDate,
                    endDate: this.props.exitDate ? this.props.exitDate : null,
                    fName: '',
                    mName: '',
                    lName: '',
                    passholderType: types.ADULT_FEE_TYPE,
                    alternateGroupLeaderId: '',
                    email: '',
                };
                newGroupMembers.push(newMember);
            }
        }
        this.props.handleGroupMemberChange(newGroupMembers);
    }

    removeMember(memberId) {
        const newGroupMembers = this.props.groupMembers;
        newGroupMembers.splice(memberId, 1);

        this.props.handleGroupMemberChange(newGroupMembers);
    }

    handleMemberInfoChange(idx, field, input) {
        const newGroupMembers = this.props.groupMembers;
        const foundMember = newGroupMembers[idx];

        if (foundMember) {
            switch (field) {
                case 'memberFName':
                    foundMember.fName = input;
                    break;
                case 'memberMName':
                    foundMember.mName = input;
                    break;
                case 'memberLName':
                    foundMember.lName = input;
                    break;
                case 'memberArriving':
                    foundMember.startDate = input;
                    break;
                case 'memberDeparting':
                    foundMember.endDate = input;
                    break;
                case 'memberPassholderId':
                    foundMember.passholderId = input;
                    break;
                case 'memberPassholderType':
                    foundMember.passholderType = input;
                    break;
                case 'memberAlternateGroupLeaderId':
                    // also empty the email on unclick
                    if (input === '') foundMember.email = '';
                    foundMember.alternateGroupLeaderId = input;
                    break;
                case 'memberEmail':
                    foundMember.email = input;
                    break;
                default:
                    break;
            }

            this.props.handleGroupMemberChange(newGroupMembers);
        }
    }

    onOpenMessageModal() {
        this.setState({ isMessageModalOpen: true });
    }

    onCloseMessageModal() {
        this.setState({ isMessageModalOpen: false });
    }

    renderLeader() {
        const leader = this.props.groupMembers[0];

        if (!leader) {
            return null;
        }

        return (
            <div>
                <div className="lead">
                    Permit Holder/Group Member: <b>{`${leader.fName} ${leader.lName}`}</b>
                </div>
                <FeeTypeInfo
                    index={0}
                    division={this.props.division}
                    member={this.props.groupMembers[0]}
                    handleMemberInfoChange={this.handleMemberInfoChange}
                />
            </div>
        );
    }

    renderOthers() {
        if (this.props.groupMembers.length <= 1) {
            return null;
        }

        return this.props.groupMembers
            .slice(1)
            .map((member, index) => (
                <GroupMemberItem
                    key={index + 1}
                    member={member}
                    index={index + 1}
                    permit={this.props.permit}
                    division={this.props.division}
                    handleMemberInfoChange={this.handleMemberInfoChange}
                    altGroupLeaders={this.props.altGroupLeaders}
                    maxAlternateLeaders={this.props.maxAlternateLeaders}
                    issuance={this.props.issuance}
                    focusOnNextAdd={this.state.focusOnNextAdd}
                    isDisabled={this.props.isDisabled}
                    removeMember={this.removeMember}
                    entryDate={this.props.entryDate}
                    dateMap={createAvailabilityRangeMap(
                        this.props.entryDate,
                        this.props.exitDate
                    )}
                />
            ));
    }

    render() {
        const hasAltEmailEntered = !isEmpty(this.props.altGroupLeaders);
        const isResCreation =
            this.props.issuance.status ===
                issuanceStatuses.get(types.PERMIT_PRELIMINARY) ||
            this.props.issuance.status === issuanceStatuses.get(types.PERMIT_IN_PROGRESS);

        return (
            <div className="boundarywaters-group-member-list rec-nested-wrap">
                <div className="rec-grid-12-12">
                    <Heading headingLevel={2} appearance="h5">
                        Group Members
                    </Heading>
                    <Text fontWeight="bold" className="my-1">
                        Permits are not transferable (permit holder, alternates, entry
                        date and entry point cannot be changed once the permit reservation
                        is made).
                    </Text>
                </div>
                <div className="rec-grid-12-12 mb-1">
                    <div className="boundarywaters-group-member-list-row-wrapper">
                        <NumberField
                            id="memberCounterButtonGroup"
                            units="members"
                            visualLabel="Number of group members"
                            min={1}
                            max={this.props.maxMemberQuota}
                            value={this.state.memberCount}
                            onChange={(count) => {
                                if (count > this.state.memberCount) {
                                    this.addMember();
                                } else {
                                    this.removeMember(count);
                                }
                            }}
                        />
                        <Button
                            mode="link"
                            onClick={this.onOpenMessageModal}
                            appearance="link"
                            iconBeforeElement={<Icon iconName="help" />}
                        >
                            {' '}
                            What&apos;s an alternate group leader?
                        </Button>
                    </div>
                    <StyledModal
                        shouldShowCloseButton={false}
                        shouldActionsStickInMobile
                        size="md"
                        isOpen={this.state.isMessageModalOpen}
                        heading='"What&apos;s an Alternate Group Leader?"'
                        onRequestClose={this.onCloseMessageModal}
                    >
                        <Text>
                            Alternate Group Leaders are group members that you designate
                            to pick up the permit if you are unavailable. You can only
                            designate alternates at the time of purchase. You can
                            designate up to 3. After purchase, you cannot change any
                            information for an alternate. You may delete the alternate
                            after purchase and before getting the permit issued, but you
                            will not be able to designate any other alternates.
                        </Text>

                        <ModalActions>
                            <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                                <Button
                                    onClick={this.onCloseMessageModal}
                                    appearance="tertiary"
                                >
                                    Close
                                </Button>
                            </ButtonGroup>
                        </ModalActions>
                    </StyledModal>
                </div>
                <div className="rec-grid-12-12 group-member-list">
                    <div className="rec-grid-12-12">
                        {this.renderLeader()}
                        {this.renderOthers()}
                    </div>
                </div>
                <div className="rec-grid-12-12">
                    <Button
                        isDisabled={this.state.memberCount >= this.props.maxMemberQuota}
                        iconBeforeElement={<Icon iconName="add-circle" />}
                        appearance="link"
                        onClick={() => this.addMember()}
                    >
                        Add another member
                    </Button>
                </div>
                {!hasAltEmailEntered && isResCreation && (
                    <Box padding="md">
                        <Alert type="info" hasAriaLive>
                            You have not selected any Alternate Permit Holders who can
                            pick up the permit if the permit holder cannot go. Alternates
                            cannot be added once a reservation has been made. Check the
                            box under a group member&apos;s name to designate an
                            alternate.
                        </Alert>
                    </Box>
                )}
            </div>
        );
    }
}

export default GroupMemberList;
